import { useEffect, useState } from "react";
import apiService from "../../../services/service";
import API_ROUTES from "../../../api-routes";
import useDebounce from "~/hooks/useDebounce";
import { z } from "zod";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import useFileUpload from "~/utils/useFileUpload";
function useCoreEntityAttachments() {
  const entityData = useSelector((state) => state.generalInformation);

  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState(10);
  const [first, setFirst] = useState(0);
  const [isDataAvailable, setIsDataAvailable] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [isAuditSidebarVisible, setAuditSidebarVisible] = useState(false);
  const [attachmentConfirmModal, setAttachmentConfirmModal] = useState(false);
  const [temporaryMessage, setTemporaryMessage] = useState("");
  const [isAttachmentLoader, setIsAttachmentLoader] = useState(false);
  const [selectedFile, setSelectedFile] = useState({});
  const [restoreMessage, setRstoreMessage] = useState("");

  const { uploadFile, updateTag, uuidToUlid, getFileVersion } = useFileUpload();
  const { id: userId } = useSelector((state) => state.login);

  const [isFileUploaded, setIsFileUploaded] = useState(true);
  const { id } = useParams();
  const allServices = useSelector(
    (state) => state.globalEntities.entityservices,
  );
  const getService = () => {
    const service = allServices.find((service) => {
      return service?.id === {}?.service;
    });
    return service;
  };
  const fileSupported = [
    "xls",
    "csv",
    "xml",
    "pdf",
    "jpg",
    "jpeg",
    "png",
    "txt",
    "doc",
    "docx",
    "xlsx",
  ];
  const uploadFileHandler = async (files) => {
    setIsFileUploaded(false);

    try {
      const formData = new FormData();
      const fileOutput = [];
      const attachments = [];
      let tags, metadata;
      for (let i = 0; i < files.length; i++) {
        const file = files[i]?.file;
        formData.append("file", file);
        let fileData = {
          fileName: file?.fileName,
          category: file?.category,
          subCategory: file?.subcategory,
        };
        if (file?.description) {
          fileData.description = file?.description;
        }
        fileOutput.push(fileData);
        formData.append("filesData", JSON.stringify(fileOutput));

        tags = {
          entity: uuidToUlid(entityData?.generalInformation?.data?.id),
          country: entityData?.generalInformation?.data?.country,
          core_entity_info: "Core Entity Information",
          temporary_file: "false",
          category: file?.category,
          subCategory: file?.subcategory,
          year: new Date().getFullYear().toString(),
        };
        const version = `${await getFileVersion(tags)}`;
        metadata = {
          original_file_name: encodeURIComponent(file.name),
          file_size: file.size.toString(),
          file_type: file.type,
          user: userId,
          file_name: file.fileName,
          version,
          created_at: new Date().toISOString(),
          file_object_type: "ENTITIES_PLUS",
          ...(file?.description && {
            description: encodeURIComponent(file?.description),
          }),
        };
        const uploadData = await uploadFile(file);
        metadata = { ...metadata, url: uploadData.url };
        attachments.push({ url: uploadData.url, fileName: uploadData.id });
        await updateTag(uploadData.id, tags, metadata);
        await apiService(API_ROUTES.ADD_DOCUMENT_AUDIT, "POST", {
          body: { attachments },
          replace: { id, type: "ENTITIES_PLUS" },
          schema: z.object({
            id: z.string().uuid(),
            type: z.enum(["ENTITIES_PLUS"]),
          }),
        });
      }
    } catch (e) {
      setIsFileUploaded(false);
      console.error(e);
    } finally {
      // Introduced a delay to allow time for the virus scan to complete after file upload,
      // ensuring the document list fetch reflects the latest and clean files.
      setTimeout(async () => {
        await fetchOfficerAttachments();
        setSuccessMessage("File uploaded successfully");
        setIsFileUploaded(true);
        setShowUploadModal(false);
      }, 2000);
    }
  };
  const fetchOfficerAttachments = async () => {
    const { data } = await apiService(API_ROUTES.FETCH_DOCUMENTS, "GET", {
      replace: { id, type: "ENTITIES_PLUS" },
    });
    if (data?.length) {
      setIsDataAvailable(true);
      setAttachments(data);
    } else {
      setIsDataAvailable(false);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchOfficerAttachments();
  }, []);

  const handleChangePage = (e) => {
    setPage(e.page);
    setRows(e.rows);
    setFirst(e.first);
  };

  const handleOpenDialog = (detail) => {
    setSelectedUser(detail);
    setOpenDialog(true);
  };

  const handleDeleteDocument = (file) => {
    setAttachmentConfirmModal(true);
    setSelectedFile(file);
  };
  const handleConfirmDelete = async () => {
    setIsAttachmentLoader(true);
    setRstoreMessage("");
    setTemporaryMessage("");

    const { status } = await apiService(
      API_ROUTES.DELETE_ATTACHMENT,
      "DELETE",
      {
        body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
        replace: { relatedId: id, relatedType: "ENTITIES_PLUS" },
        schema: z.object({
          relatedId: z.string().uuid(),
          relatedType: z.enum(["ENTITIES_PLUS"]),
        }),
      },
    );
    if (status) {
      setTimeout(async () => {
        setLoading(true);
        await fetchOfficerAttachments();
        setAttachmentConfirmModal(false);
        setTemporaryMessage(`File ${selectedFile?.name} has been deleted.`);
        setIsAttachmentLoader(false);
      }, 2000);
    }
  };
  const handleRestoreAttachment = async () => {
    setLoading(true);
    setTemporaryMessage("");
    setRstoreMessage("");
    const { status } = await apiService(API_ROUTES.RESTORE_ATTACHMENT, "POST", {
      body: { blobUrl: selectedFile?.blob_url, name: selectedFile?.name },
    });
    if (status) {
      setTimeout(async () => {
        await fetchOfficerAttachments();
        setRstoreMessage(`${selectedFile?.name} has been restored.`);
      }, 2000);
    }
  };
  return {
    openDialog,
    setOpenDialog,
    selectedUser,
    setSelectedUser,
    attachments,
    loading,
    page,
    rows,
    totalRecords,
    onPageChange: handleChangePage,
    handleOpenDialog,
    isDataAvailable,
    entitydata: entityData?.generalInformation?.data,
    showUploadModal,
    fileSupported,
    setShowUploadModal,
    uploadFileHandler,
    getService,
    requestdata: [],
    isFileUploaded,
    successMessage,
    setSuccessMessage,
    isAuditSidebarVisible,
    setAuditSidebarVisible,
    first,
    handleDeleteDocument,
    attachmentConfirmModal,
    setAttachmentConfirmModal,
    handleConfirmDelete,
    handleRestoreAttachment,
    temporaryMessage,
    isAttachmentLoader,
    setTemporaryMessage,
    restoreMessage,
    setRstoreMessage,
  };
}

export default useCoreEntityAttachments;
